import TemplateFormFieldValueModel from '../models/templating/api/TemplateFormFieldValueModel';
import MenuItemModel from '../models/nlp/menu/MenuItemModel';
import { SyncMenusRequest } from '../models/nlp/menu/SyncMenusRequest';

// "differs" means the location's menu doesn't match the primary; "error" means it couldn't be loaded
export type LocationSyncStatus = "syncing" | "synced" | "differs" | "error";

export interface SyncLocation {
    liveApplicationId: string
    manageMenuApplicationId?: string
    name: string
    isPrimary: boolean
}

export interface MenuDifferences {
    // primary items the location doesn't have
    missing: string[]
    // location items the primary doesn't have
    extra: string[]
    // items whose "Referred to As" name doesn't match the primary
    renamed: string[]
}

export interface LocationSyncResult extends SyncLocation {
    status: LocationSyncStatus
    differences?: MenuDifferences
}

const parseMenuConfiguration = (value?: string): SyncMenusRequest["menuConfiguration"] | null => {
    if (!value?.trim().startsWith("{")) return null;
    try {
        const parsed = JSON.parse(value);
        return parsed?.menuConfiguration?.primaryLocation ? parsed.menuConfiguration : null;
    } catch {
        return null;
    }
}

const getLocationsFromConfig = (config: SyncMenusRequest["menuConfiguration"]): SyncLocation[] => {
    const primary = config.primaryLocation;
    return [
        { liveApplicationId: primary.liveApplicationId, manageMenuApplicationId: primary.manageMenuApplicationId, name: primary.liveApplicationName, isPrimary: true },
        ...(config.secondaryLocations ?? []).map(location => ({
            liveApplicationId: location.liveApplicationId,
            manageMenuApplicationId: location.manageMenuApplicationId,
            name: location.liveApplicationName,
            isPrimary: false
        }))
    ].filter(location => location.name?.length);
}

/**
 * Reads the locations covered by the menu sync module from its form values,
 * when they hold the generated menu sync configuration JSON inline.
 */
export const getSyncLocations = (fieldValues: TemplateFormFieldValueModel[] = []): SyncLocation[] => {
    for (const field of fieldValues) {
        const config = parseMenuConfiguration(field?.value);
        if (config) return getLocationsFromConfig(config);
    }
    return [];
}

const fetchJson = async (url: string) => (await fetch(url)).json();

/**
 * Like getSyncLocations, but also downloads the configuration when the module
 * stores it as an uploaded .json file (the file upload field holds its URL).
 */
export const loadSyncLocations = async (fieldValues: TemplateFormFieldValueModel[] = [], loadJson: (url: string) => Promise<any> = fetchJson): Promise<SyncLocation[]> => {
    const inlineLocations = getSyncLocations(fieldValues);
    if (inlineLocations.length) return inlineLocations;
    for (const field of fieldValues) {
        const url = field?.value?.trim();
        if (!url || !/^https?:\/\/\S+\.json(\?\S*)?$/i.test(url)) continue;
        try {
            const json = await loadJson(url);
            if (json?.menuConfiguration?.primaryLocation) return getLocationsFromConfig(json.menuConfiguration);
        } catch {
            // an unreadable file just means we can't list the locations
        }
    }
    return [];
}

const normalize = (value?: string) => value?.trim().toLowerCase() ?? "";
const spokenName = (item: MenuItemModel) => item.colloquialName?.trim() || item.originalName?.trim() || "";

/**
 * Compares a location's menu items with the primary's by item name. Names a location
 * keeps on purpose (colloquialName marked location specific) aren't reported.
 * Menus can have several items with the same original name (e.g. two "Burger" records
 * referred to as "Lunch Burger" and "Dinner Burger"), so items are matched one-to-one:
 * same-named items with the same spoken name pair up first, then the rest pair up in order.
 */
export const compareMenus = (primaryItems: MenuItemModel[] = [], locationItems: MenuItemModel[] = []): MenuDifferences => {
    const unmatchedByName = new Map<string, MenuItemModel[]>();
    for (const item of locationItems) {
        if (!item?.originalName) continue;
        const key = normalize(item.originalName);
        unmatchedByName.set(key, [...(unmatchedByName.get(key) ?? []), item]);
    }
    const takeCandidate = (item: MenuItemModel, sameSpokenNameOnly: boolean) => {
        const candidates = unmatchedByName.get(normalize(item.originalName)) ?? [];
        const index = sameSpokenNameOnly ? candidates.findIndex(c => normalize(spokenName(c)) === normalize(spokenName(item))) : (candidates.length ? 0 : -1);
        return index === -1 ? null : candidates.splice(index, 1)[0];
    };

    const namedPrimaryItems = primaryItems.filter(item => item?.originalName);
    const exactMatches = new Set(namedPrimaryItems.filter(item => takeCandidate(item, true)));
    const missing: string[] = [];
    const renamed: string[] = [];
    for (const item of namedPrimaryItems) {
        if (exactMatches.has(item)) continue;
        const match = takeCandidate(item, false);
        if (!match) {
            missing.push(item.originalName);
        } else if (!match.locationSpecificPersistentProperties?.includes("colloquialName")) {
            renamed.push(item.originalName);
        }
    }
    const extra = [...unmatchedByName.values()].flat().map(item => item.originalName);
    return { missing, extra, renamed };
}

// Each location's item list comes from its own POS, so missing and extra items are expected.
// The sync aligns the names of the items they share, so only name differences mean it didn't take.
export const hasDifferences = (differences?: MenuDifferences) => !!differences?.renamed.length;

/**
 * Turns a location's comparison into its result. The primary is the source of the sync, so it always matches.
 */
export const getLocationSyncResult = (location: SyncLocation, primaryItems: MenuItemModel[], locationItems: MenuItemModel[] | null): LocationSyncResult => {
    if (location.isPrimary) return { ...location, status: "synced" };
    if (!locationItems) return { ...location, status: "error" };
    const differences = compareMenus(primaryItems, locationItems);
    return { ...location, status: hasDifferences(differences) ? "differs" : "synced", differences };
}

/**
 * Short summary of what's different, e.g. "3 names differ".
 */
export const describeDifferences = (result: LocationSyncResult): string => {
    if (result.status === "error") return "Couldn't load menu";
    const renamedCount = result.differences?.renamed.length ?? 0;
    if (!renamedCount) return "Matches primary";
    return `${renamedCount} name${renamedCount === 1 ? "" : "s"} differ${renamedCount === 1 ? "s" : ""}`;
}
